import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { 
  ExternalLink, 
  Store, 
  Package, 
  Eye, 
  Copy, 
  Check,
  ShoppingCart,
  Sparkles,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = window.location.origin;

export default function StorePage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const { data } = await axios.get(`${API}/payment-links`, {
        withCredentials: true,
      });
      setLinks(data.filter((link) => link.is_active));
    } catch (error) {
      console.error("Failed to fetch links:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const storeUrl = `${BASE_URL}/store/${user?.store_slug}`;

  const copyUrl = async () => {
    await navigator.clipboard.writeText(storeUrl);
    setCopied(true);
    toast.success("Store URL copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-10 w-48" />
            <Skeleton className="h-5 w-64" />
          </div>
          <Skeleton className="h-11 w-36" />
        </div>
        <Skeleton className="h-24 rounded-2xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-72 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="store-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{t("store.title")}</h1>
          <p className="text-muted-foreground mt-1">
            {t("store.subtitle")}
          </p>
        </div>
        <a href={storeUrl} target="_blank" rel="noopener noreferrer">
          <Button 
            className="h-11 px-6 rounded-xl shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/30 transition-all duration-300"
            data-testid="preview-store-btn"
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            {t("store.preview")}
          </Button>
        </a>
      </div>

      {/* Store URL Card */}
      <Card className="border-border overflow-hidden">
        <div className="bg-gradient-to-r from-[#0B1F3A] to-[#3A6D8C] p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="text-white">
              <p className="text-sm text-white/70 mb-1">{t("store.storeUrl")}</p>
              <p className="text-lg md:text-xl font-medium break-all">{storeUrl}</p>
            </div>
            <Button
              variant="secondary"
              className="h-11 px-6 rounded-xl shrink-0"
              onClick={copyUrl}
            >
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {t("store.copied")}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {t("store.copyUrl")}
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>

      {/* Store Preview */}
      <Card className="border-border">
        <CardHeader className="pb-4">
          <CardTitle className="text-lg flex items-center gap-2">
            <div className="p-2 rounded-lg bg-[#0B1F3A]/10 dark:bg-white/10">
              <Store className="h-5 w-5 text-[#0B1F3A] dark:text-white" />
            </div>
            {t("store.storePreview")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Creator Info */}
          <div className="flex items-center gap-4 mb-8 pb-6 border-b border-border">
            <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#0B1F3A] to-[#3A6D8C] flex items-center justify-center text-white text-2xl font-bold shadow-lg">
              {user?.name?.charAt(0).toUpperCase() || "U"}
            </div>
            <div>
              <h2 className="text-xl font-bold">{user?.name}</h2>
              {user?.bio && (
                <p className="text-muted-foreground mt-1">{user.bio}</p>
              )}
            </div>
          </div>

          {/* Products Grid */}
          {links.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-6">
                <Package className="h-10 w-10 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-semibold mb-2">{t("store.noProducts")}</h3>
              <p className="text-muted-foreground mb-8 max-w-sm mx-auto">
                {t("store.createToShow")}
              </p>
              <Link to="/payment-links">
                <Button className="h-12 px-8 rounded-xl shadow-lg shadow-primary/20">
                  <Sparkles className="h-5 w-5 mr-2" />
                  {t("dashboard.createPaymentLink")}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {links.map((link, index) => (
                <div
                  key={link.id}
                  className="group relative rounded-2xl border border-border overflow-hidden bg-card hover:shadow-xl hover:shadow-[#0B1F3A]/5 dark:hover:shadow-white/5 transition-all duration-300"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  {/* Preview Image */}
                  <div className="aspect-video bg-gradient-to-br from-[#0B1F3A]/10 to-[#3A6D8C]/10 relative overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Package className="h-12 w-12 text-muted-foreground/30 group-hover:scale-110 transition-transform duration-300" />
                    </div>
                    {link.blur_preview && (
                      <div className="absolute inset-0 backdrop-blur-md bg-black/5 flex items-center justify-center">
                        <div className="bg-black/40 backdrop-blur-sm rounded-lg px-3 py-1.5 flex items-center gap-2">
                          <Lock className="h-4 w-4 text-white" />
                          <span className="text-sm text-white font-medium">{t("store.preview")}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-semibold text-lg truncate group-hover:text-primary transition-colors">
                      {link.title}
                    </h3>
                    {link.description && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {link.description}
                      </p>
                    )}
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        {link.views}
                      </span>
                      <span className="flex items-center gap-1">
                        <ShoppingCart className="h-4 w-4" />
                        {link.sales}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
                      <span className="text-2xl font-bold">
                        {formatCurrency(link.price, link.currency)}
                      </span>
                      <a href={`/p/${link.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="rounded-xl hover:-translate-y-0.5 transition-all duration-200">
                          {link.button_text}
                        </Button>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
